import { Grid, Group, Stack, Text, Title } from '@mantine/core';
import moment from 'moment';

import { EORIGIN_DATE_FORMAT, EORIGIN_DATE_FORMAT_WITH_TIME, TransitGuaranteeCustomsQueryResponse } from '@e-origin/shared';

interface TransitGuaranteeCustomsQueryResponseDisplayProps {
  customsQueryResponse: TransitGuaranteeCustomsQueryResponse;
}

export const TransitGuaranteeCustomsQueryResponseDisplay: React.FC<TransitGuaranteeCustomsQueryResponseDisplayProps> = ({ customsQueryResponse }) => {
  if (!customsQueryResponse) {
    return null;
  }

  return (
    <Stack px="sm">
      <Title order={5} fw={600}>
        Customs Query Response
      </Title>
      <Grid>
        {customsQueryResponse.acceptanceDateAndTime && (
          <Grid.Col span={3}>
            <Stack gap={4}>
              <Text c="dimmed" size="sm">
                Acceptance Date
              </Text>
              <Text>{moment(customsQueryResponse.acceptanceDateAndTime).format(EORIGIN_DATE_FORMAT_WITH_TIME)}</Text>
            </Stack>
          </Grid.Col>
        )}
        {customsQueryResponse.guaranteeType && (
          <Grid.Col span={3}>
            <Stack gap={4}>
              <Text c="dimmed" size="sm">
                Guarantee Type
              </Text>
              <Text>{customsQueryResponse.guaranteeType}</Text>
            </Stack>
          </Grid.Col>
        )}
        {customsQueryResponse.guaranteeMonitoringCode && (
          <Grid.Col span={3}>
            <Stack gap={4}>
              <Text c="dimmed" size="sm">
                Guarantee Monitoring Code
              </Text>
              <Text>{customsQueryResponse.guaranteeMonitoringCode}</Text>
            </Stack>
          </Grid.Col>
        )}
        {customsQueryResponse.officeOfGuaranteeReference && (
          <Grid.Col span={3}>
            <Stack gap={4}>
              <Text c="dimmed" size="sm">
                Office of Guarantee Reference
              </Text>
              <Text>{customsQueryResponse.officeOfGuaranteeReference}</Text>
            </Stack>
          </Grid.Col>
        )}
      </Grid>

      {customsQueryResponse.usage?.length && (
        <Stack mt="sm">
          <Title order={6} fw="bold">
            Usage
          </Title>
          {customsQueryResponse.usage.map((usage, index) => (
            <Grid key={index}>
              <Grid.Col span={2}>
                <Stack gap={4}>
                  <Text c="dimmed" size="sm">
                    Sequence Number
                  </Text>
                  <Text>{usage.sequenceNumber}</Text>
                </Stack>
              </Grid.Col>
              {usage.MRN && (
                <Grid.Col span={3}>
                  <Stack gap={4}>
                    <Text c="dimmed" size="sm">
                      MRN
                    </Text>
                    <Text>{usage.MRN}</Text>
                  </Stack>
                </Grid.Col>
              )}
              {usage.coveredAmount !== undefined && usage.coveredAmount !== null && (
                <Grid.Col span={2}>
                  <Stack gap={4}>
                    <Text c="dimmed" size="sm">
                      Covered Amount
                    </Text>
                    <Text>
                      {usage.coveredAmount} {usage.currency}
                    </Text>
                  </Stack>
                </Grid.Col>
              )}
              {usage.lockDate && (
                <Grid.Col span={2}>
                  <Stack gap={4}>
                    <Text c="dimmed" size="sm">
                      Lock Date
                    </Text>
                    <Text>{moment(usage.lockDate).format(EORIGIN_DATE_FORMAT_WITH_TIME)}</Text>
                  </Stack>
                </Grid.Col>
              )}
              {usage.arrivalDateAndTime && (
                <Grid.Col span={2}>
                  <Stack gap={4}>
                    <Text c="dimmed" size="sm">
                      Arrival Date
                    </Text>
                    <Text>{moment(usage.arrivalDateAndTime).format(EORIGIN_DATE_FORMAT_WITH_TIME)}</Text>
                  </Stack>
                </Grid.Col>
              )}
              {usage.releaseDate && (
                <Grid.Col span={2}>
                  <Stack gap={4}>
                    <Text c="dimmed" size="sm">
                      Release Date
                    </Text>
                    <Text>{moment(usage.releaseDate).format(EORIGIN_DATE_FORMAT_WITH_TIME)}</Text>
                  </Stack>
                </Grid.Col>
              )}
            </Grid>
          ))}
        </Stack>
      )}

      {customsQueryResponse.exposure && (
        <Stack mt="sm">
          <Title order={6} fw="bold">
            Exposure
          </Title>
          <Grid>
            <Grid.Col span={3}>
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  Exposure
                </Text>
                <Text>
                  {customsQueryResponse.exposure.exposure} {customsQueryResponse.exposure.currency}
                </Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={3}>
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  Exposure Counter
                </Text>
                <Text>{customsQueryResponse.exposure.exposureCounter}</Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={3}>
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  Balance
                </Text>
                <Text>
                  {customsQueryResponse.exposure.balance} {customsQueryResponse.exposure.currency}
                </Text>
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      )}

      {customsQueryResponse.guarantor && (
        <Stack mt="sm">
          <Title order={6} fw="bold">
            Guarantor
          </Title>
          {(customsQueryResponse.guarantor.name || customsQueryResponse.guarantor.identificationNumber) && (
            <Group gap="xl">
              {customsQueryResponse.guarantor.name && (
                <Stack gap={4}>
                  <Text c="dimmed" size="sm">
                    Name
                  </Text>
                  <Text>{customsQueryResponse.guarantor.name}</Text>
                </Stack>
              )}
              {customsQueryResponse.guarantor.identificationNumber && (
                <Stack gap={4}>
                  <Text c="dimmed" size="sm">
                    Identification Number
                  </Text>
                  <Text>{customsQueryResponse.guarantor.identificationNumber}</Text>
                </Stack>
              )}
            </Group>
          )}
          {customsQueryResponse.guarantor.address && (
            <Group gap="xl">
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  Street and Number
                </Text>
                <Text>{customsQueryResponse.guarantor.address.streetAndNumber}</Text>
              </Stack>
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  Postcode
                </Text>
                <Text>{customsQueryResponse.guarantor.address.postcode}</Text>
              </Stack>
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  City
                </Text>
                <Text>{customsQueryResponse.guarantor.address.city}</Text>
              </Stack>
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  Country
                </Text>
                <Text>{customsQueryResponse.guarantor.address.country}</Text>
              </Stack>
            </Group>
          )}
          {customsQueryResponse.guarantor.contactPerson && (
            <Group gap="xl">
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  Contact Person
                </Text>
                <Text>
                  {customsQueryResponse.guarantor.contactPerson.name}
                  {customsQueryResponse.guarantor.contactPerson.phoneNumber && ` - ${customsQueryResponse.guarantor.contactPerson.phoneNumber}`}
                  {customsQueryResponse.guarantor.contactPerson.email && ` - ${customsQueryResponse.guarantor.contactPerson.email}`}
                </Text>
              </Stack>
            </Group>
          )}
        </Stack>
      )}

      {customsQueryResponse.owner && (
        <Stack mt="sm">
          <Title order={6} fw="bold">
            Owner
          </Title>
          {customsQueryResponse.owner.name && (
            <Group gap="xl">
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  Name
                </Text>
                <Text>{customsQueryResponse.owner.name}</Text>
              </Stack>
            </Group>
          )}
          {customsQueryResponse.owner.address && (
            <Group gap="xl">
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  Street and Number
                </Text>
                <Text>{customsQueryResponse.owner.address.streetAndNumber}</Text>
              </Stack>
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  Postcode
                </Text>
                <Text>{customsQueryResponse.owner.address.postcode}</Text>
              </Stack>
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  City
                </Text>
                <Text>{customsQueryResponse.owner.address.city}</Text>
              </Stack>
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  Country
                </Text>
                <Text>{customsQueryResponse.owner.address.country}</Text>
              </Stack>
            </Group>
          )}
        </Stack>
      )}

      {customsQueryResponse.comprehensiveGuarantee && (
        <Stack mt="sm">
          <Title order={6} fw="bold">
            Comprehensive Guarantee
          </Title>
          <Group gap="xl">
            <Stack gap={4}>
              <Text c="dimmed" size="sm">
                Reference Amount
              </Text>
              <Text>
                {customsQueryResponse.comprehensiveGuarantee.referenceAmount} {customsQueryResponse.comprehensiveGuarantee.currency}
              </Text>
            </Stack>
            <Stack gap={4}>
              <Text c="dimmed" size="sm">
                Percentage
              </Text>
              <Text>{customsQueryResponse.comprehensiveGuarantee.percentageOfReferenceAmount}%</Text>
            </Stack>
            <Stack gap={4}>
              <Text c="dimmed" size="sm">
                Guarantee Amount
              </Text>
              <Text>
                {customsQueryResponse.comprehensiveGuarantee.guaranteeAmount} {customsQueryResponse.comprehensiveGuarantee.currency}
              </Text>
            </Stack>
            <Stack gap={4}>
              <Text c="dimmed" size="sm">
                Number of Certificates
              </Text>
              <Text>{customsQueryResponse.comprehensiveGuarantee.numberOfCertificates}</Text>
            </Stack>
          </Group>
          <Group gap="xl">
            {customsQueryResponse.comprehensiveGuarantee.validityStartDate && (
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  Validity Start Date
                </Text>
                <Text>{moment(customsQueryResponse.comprehensiveGuarantee.validityStartDate).format(EORIGIN_DATE_FORMAT)}</Text>
              </Stack>
            )}
            {customsQueryResponse.comprehensiveGuarantee.validityEndDate && (
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  Validity End Date
                </Text>
                <Text>{moment(customsQueryResponse.comprehensiveGuarantee.validityEndDate).format(EORIGIN_DATE_FORMAT)}</Text>
              </Stack>
            )}
            {customsQueryResponse.comprehensiveGuarantee.liabilityLiberationDate && (
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  Liability Liberation Date
                </Text>
                <Text>{customsQueryResponse.comprehensiveGuarantee.liabilityLiberationDate}</Text>
              </Stack>
            )}
          </Group>
          {(customsQueryResponse.comprehensiveGuarantee.invalidityReasonCode || customsQueryResponse.comprehensiveGuarantee.invalidityReasonText) && (
            <Group gap="xl">
              {customsQueryResponse.comprehensiveGuarantee.invalidityReasonCode && (
                <Stack gap={4}>
                  <Text c="dimmed" size="sm">
                    Invalidity Reason Code
                  </Text>
                  <Text>{customsQueryResponse.comprehensiveGuarantee.invalidityReasonCode}</Text>
                </Stack>
              )}
              {customsQueryResponse.comprehensiveGuarantee.invalidityReasonText && (
                <Stack gap={4}>
                  <Text c="dimmed" size="sm">
                    Invalidity Reason Text
                  </Text>
                  <Text>{customsQueryResponse.comprehensiveGuarantee.invalidityReasonText}</Text>
                </Stack>
              )}
            </Group>
          )}
          {customsQueryResponse.comprehensiveGuarantee.validityLimitation && (
            <Group gap="xl">
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  Validity Limitation Sequence
                </Text>
                <Text>{customsQueryResponse.comprehensiveGuarantee.validityLimitation.sequenceNumber}</Text>
              </Stack>
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  Guarantee Not Valid In
                </Text>
                <Text>{customsQueryResponse.comprehensiveGuarantee.validityLimitation.guaranteeNotValidIn}</Text>
              </Stack>
            </Group>
          )}
          <Group gap="xl">
            <Stack gap={4}>
              <Text c="dimmed" size="sm">
                Restricted use for suspended goods
              </Text>
              <Text>{customsQueryResponse.comprehensiveGuarantee.restrictedUseForSuspendedGoods ?? 'N/A'}</Text>
            </Stack>
          </Group>
        </Stack>
      )}

      {customsQueryResponse.individualGuaranteeByGuarantor && (
        <Stack mt="sm">
          <Title order={6} fw="bold">
            Individual Guarantee By Guarantor
          </Title>
          <Grid>
            <Grid.Col span={3}>
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  Guarantee Amount
                </Text>
                <Text>
                  {customsQueryResponse.individualGuaranteeByGuarantor.guaranteeAmount} {customsQueryResponse.individualGuaranteeByGuarantor.currency}
                </Text>
              </Stack>
            </Grid.Col>
            {customsQueryResponse.individualGuaranteeByGuarantor.officeOfDepartureReference && (
              <Grid.Col span={4}>
                <Stack gap={4}>
                  <Text c="dimmed" size="sm">
                    Office of Departure Reference
                  </Text>
                  <Text>{customsQueryResponse.individualGuaranteeByGuarantor.officeOfDepartureReference}</Text>
                </Stack>
              </Grid.Col>
            )}
            {customsQueryResponse.individualGuaranteeByGuarantor.officeOfDestinationReference && (
              <Grid.Col span={4}>
                <Stack gap={4}>
                  <Text c="dimmed" size="sm">
                    Office of Destination Reference
                  </Text>
                  <Text>{customsQueryResponse.individualGuaranteeByGuarantor.officeOfDestinationReference}</Text>
                </Stack>
              </Grid.Col>
            )}
          </Grid>
        </Stack>
      )}

      {customsQueryResponse.individualGuaranteeVoucher && (
        <Stack mt="sm">
          <Title order={6} fw="bold">
            Individual Guarantee Voucher
          </Title>
          <Grid>
            {customsQueryResponse.individualGuaranteeVoucher.issueDate && (
              <Grid.Col span={2}>
                <Stack gap={4}>
                  <Text c="dimmed" size="sm">
                    Issue Date
                  </Text>
                  <Text fw={500}>{moment(customsQueryResponse.individualGuaranteeVoucher.issueDate).format('YYYY-MM-DD')}</Text>
                </Stack>
              </Grid.Col>
            )}
            {customsQueryResponse.individualGuaranteeVoucher.expiryDate && (
              <Grid.Col span={2}>
                <Stack gap={4}>
                  <Text c="dimmed" size="sm">
                    Expiry Date
                  </Text>
                  <Text>{moment(customsQueryResponse.individualGuaranteeVoucher.expiryDate).format('YYYY-MM-DD')}</Text>
                </Stack>
              </Grid.Col>
            )}
            <Grid.Col span={2}>
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  Copy Given
                </Text>
                <Text>{customsQueryResponse.individualGuaranteeVoucher.copyGiven}</Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={2}>
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  TIR Carnet
                </Text>
                <Text>{customsQueryResponse.individualGuaranteeVoucher.TIRCarnet}</Text>
              </Stack>
            </Grid.Col>

            <Grid.Col span={3}>
              <Stack gap={4}>
                <Text c="dimmed" size="sm">
                  Voucher Amount
                </Text>
                <Text>
                  {customsQueryResponse.individualGuaranteeVoucher.voucherAmount} {customsQueryResponse.individualGuaranteeVoucher.currency}
                </Text>
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      )}
    </Stack>
  );
};
